import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals'
import './styles/index.css'
import App from './App.tsx'

// ─── Initialisation Sentry ────────────────────────────────────────────────────
// Doit être appelé le plus tôt possible, avant le rendu React.
Sentry.init({
  // DSN fourni par Sentry (Settings → Projects → Client Keys)
  dsn: import.meta.env.VITE_SENTRY_DSN,

  // "development" | "production" | "staging" — injecté automatiquement par Vite
  environment: import.meta.env.MODE,

  integrations: [
    // Trace automatique des requêtes fetch/XHR et de la navigation React Router
    Sentry.browserTracingIntegration(),
    // Enregistrement vidéo de la session lors d'une erreur
    Sentry.replayIntegration(),
  ],

  // 100 % des transactions tracées en dev, 20 % en prod pour limiter le quota
  tracesSampleRate: import.meta.env.MODE === 'production' ? 0.2 : 1.0,

  // Domaines autorisés à recevoir les en-têtes de tracing distribué (CORS)
  tracePropagationTargets: ['localhost', /^https:\/\/api\.tariqgo\.com/],

  // 100 % des sessions avec erreur sont rejouées (Replay)
  replaysOnErrorSampleRate: 1.0,

  // En dev, on ne capture aucune session "normale" pour éviter le bruit
  replaysSessionSampleRate: import.meta.env.MODE === 'production' ? 0.05 : 0,
})

// ─── Core Web Vitals → Sentry ─────────────────────────────────────────────────
// Chaque métrique est envoyée comme événement Sentry de niveau "info"
// pour les visualiser dans Performance > Custom Metrics.
function sendToSentry({ name, value }: { name: string; value: number }) {
  Sentry.captureEvent({
    message: `Web Vital: ${name}`,
    level: 'info',
    tags: { metric: name },
    extra: { value },
  })
}

onCLS(sendToSentry)   // Cumulative Layout Shift
onFCP(sendToSentry)   // First Contentful Paint
onINP(sendToSentry)   // Interaction to Next Paint (replaces FID)
onLCP(sendToSentry)   // Largest Contentful Paint
onTTFB(sendToSentry)  // Time to First Byte

// ─── Rendu de l'application ───────────────────────────────────────────────────
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
